import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import React from 'react';

const featuredServices = [
  {
    title: 'iGaming & FX Licensing',
    description:
      'End-to-end licensing services for gaming operators and forex brokers across Malta, Cyprus, and key jurisdictions.',
    image:
      'https://public.readdy.ai/ai/img_res/642141cda54b38137f8b21f02c1202c7.jpg',
  },
  {
    title: 'AML & KYC Outsourcing',
    description:
      'Comprehensive Anti-Money Laundering and Know Your Customer services ensuring regulatory compliance.',
    image:
      'https://readdy.ai/api/search-image?query=Modern%20abstract%20shield%20protection%20concept%20with%20geometric%20patterns%20and%20flowing%20lines%20in%20deep%20navy%20and%20amber%20gold%20colors%20representing%20security%20and%20compliance%20professional%20corporate%20aesthetic&width=600&height=400&seq=compliance1&orientation=landscape',
  },
  {
    title: 'Compliance Outsourcing',
    description:
      'Full compliance support including MLRO services, regulatory reporting, and policy development.',
    image:
      'https://readdy.ai/api/search-image?query=Business%20professionals%20in%20meeting%20reviewing%20documents%20with%20teamwork%20and%20collaboration%20modern%20office%20setting%20professional%20corporate%20environment%20warm%20lighting&width=600&height=400&seq=compliance2&orientation=landscape',
  },
];

export default function Services() {
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: gridRef, isVisible: gridVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: extraRef, isVisible: extraVisible } = useScrollAnimation({ threshold: 0.1 });

  return (
    <section id="services" className="py-16 lg:py-20 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div
          ref={headerRef as React.RefObject<HTMLDivElement>}
          className={`text-center mb-6 transition-all duration-700 ${
            headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <span className="inline-block px-3 py-1 bg-white/60 border border-slate-900/10 text-slate-900/70 text-sm font-semibold uppercase tracking-widest rounded">
            Our Services
          </span>
        </div>

        <h2
          className={`text-4xl sm:text-5xl lg:text-6xl font-semibold text-slate-900 text-center leading-tight mb-4 transition-all duration-700 delay-100 ${
            headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          Your Growth, Backed by Expertise
        </h2>

        <p
          className={`text-lg text-slate-600 text-center max-w-3xl mx-auto mb-12 transition-all duration-700 delay-200 ${
            headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          From licensing to compliance outsourcing, we provide comprehensive
          solutions tailored to your business needs.
        </p>

        {/* Featured Services Grid - Optimized with lazy loading */}
        <div
          ref={gridRef as React.RefObject<HTMLDivElement>}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mb-10"
        >
          {featuredServices.map((service, index) => (
            <Link
              key={index}
              to="/services"
              style={{ transitionDelay: `${index * 120}ms` }}
              className={`group relative block h-80 lg:h-96 rounded-xl overflow-hidden transition-all duration-500 hover:scale-[1.02] hover:shadow-2xl cursor-pointer ${
                gridVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
            >
              <div className="absolute inset-0 overflow-hidden">
                <img
                  src={service.image}
                  alt={service.title}
                  width={600}
                  height={400}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/10 group-hover:from-black/70 group-hover:via-black/30 transition-all duration-300"></div>
              </div>
              <div className="relative h-full flex flex-col justify-end p-6 lg:p-8">
                <h3 className="text-2xl lg:text-3xl font-semibold text-white leading-tight mb-2">
                  {service.title}
                </h3>
                <p className="text-white/80 text-sm lg:text-base opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  {service.description}
                </p>
              </div>
            </Link>
          ))}
        </div>

        {/* Additional Services Preview */}
        <div
          ref={extraRef as React.RefObject<HTMLDivElement>}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10"
        >
          {[
            { title: 'Tax & Payroll', icon: 'ri-money-dollar-circle-line' },
            { title: 'Accounting', icon: 'ri-calculator-line' },
            { title: 'Legal Drafting', icon: 'ri-file-text-line' },
            { title: 'Company Formation', icon: 'ri-building-2-line' },
          ].map((item, index) => (
            <Link
              key={index}
              to="/services"
              style={{ transitionDelay: `${index * 80}ms` }}
              className={`group bg-white rounded-xl p-6 text-center hover:shadow-lg transition-all duration-500 cursor-pointer ${
                extraVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              <div className="w-12 h-12 mx-auto mb-3 bg-amber-100 rounded-full flex items-center justify-center group-hover:bg-amber-500 transition-colors">
                <i
                  className={`${item.icon} text-2xl text-amber-500 group-hover:text-white transition-colors`}
                ></i>
              </div>
              <h4 className="text-slate-900 font-semibold text-sm lg:text-base">{item.title}</h4>
            </Link>
          ))}
        </div>

        {/* Browse All Link */}
        <div className={`text-center transition-all duration-700 delay-300 ${extraVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <Link
            to="/services"
            className="inline-flex items-center gap-2 px-8 py-4 bg-slate-900 text-white text-lg font-semibold rounded-lg hover:bg-slate-800 transition-colors cursor-pointer whitespace-nowrap"
          >
            <span>Browse All Services</span>
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}
